import { Skeleton } from '@components/ui/skeleton';
import { cn } from '@lib/utils';
import type { FC } from 'react';

type SkeletonProps = {
  className?: string;
};

/**
 * Skeleton for the core mission assessment header/sidebar info
 */
export const MissionAssessmentCoreSkeleton: FC<SkeletonProps> = (props) => {
  const { className } = props;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Expert info */}
      <div className="flex items-center gap-3">
        <Skeleton className="h-12 w-12 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>

      {/* Request info */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-3 w-40" />
      </div>

      {/* Domain and use badges */}
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-6 w-24 rounded-full" />
        <Skeleton className="h-6 w-28 rounded-full" />
      </div>
    </div>
  );
};

/**
 * Skeleton for the effects section (Sankey diagram + effect cards)
 */
export const MissionAssessmentEffectsSkeleton: FC<SkeletonProps> = (props) => {
  const { className } = props;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Sankey diagram placeholder */}
      <Skeleton className="h-64 w-full rounded-lg" />

      {/* Effect cards */}
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            className="bg-[#fafafa] rounded-lg p-4"
            key={`effect-skeleton-${index}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-5 w-5 rounded" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                </div>
              </div>
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Skeleton for the inters (platform alignment) section
 */
export const MissionAssessmentIntersSkeleton: FC<SkeletonProps> = (props) => {
  const { className } = props;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Section header */}
      <Skeleton className="h-6 w-40" />

      {/* Platform cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            className="border border-gray-200 rounded-lg p-4 space-y-3"
            key={`inter-skeleton-${index}`}
          >
            <div className="flex justify-between items-start">
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
            <Skeleton className="h-16 w-full" />
            <div className="flex gap-2">
              <Skeleton className="h-5 w-12 rounded" />
              <Skeleton className="h-5 w-12 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Skeleton for the services and task recommendations section
 */
export const MissionAssessmentServicesSkeleton: FC<SkeletonProps> = (props) => {
  const { className } = props;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Task recommendations */}
      <div className="space-y-4">
        <Skeleton className="h-5 w-48" />
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton
            className="h-16 w-full rounded-md"
            key={`task-skeleton-${index}`}
          />
        ))}
      </div>

      {/* Services list */}
      <div className="space-y-4">
        <Skeleton className="h-5 w-44" />
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            className="flex items-center gap-3"
            key={`service-skeleton-${index}`}
          >
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Skeleton for the metadata section (capabilities, communities, etc.)
 */
export const MissionAssessmentMetadataSkeleton: FC<SkeletonProps> = (props) => {
  const { className } = props;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Badge lists */}
      {Array.from({ length: 3 }).map((_, sectionIndex) => (
        <div
          className="space-y-2"
          key={`metadata-section-${sectionIndex}`}
        >
          <Skeleton className="h-4 w-28" />
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 4 }).map((_, badgeIndex) => (
              <Skeleton
                className="h-6 w-20 rounded-full"
                key={`badge-skeleton-${sectionIndex}-${badgeIndex}`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Full page skeleton for when the entire MA is loading
 */
export const MissionAssessmentFullSkeleton: FC<SkeletonProps> = (props) => {
  const { className } = props;

  return (
    <div className={cn('space-y-8', className)}>
      <MissionAssessmentCoreSkeleton />
      <div className="border-t border-gray-200 pt-6">
        <MissionAssessmentServicesSkeleton />
      </div>
      <div className="border-t border-gray-200 pt-6">
        <MissionAssessmentEffectsSkeleton />
      </div>
      <div className="border-t border-gray-200 pt-6">
        <MissionAssessmentIntersSkeleton />
      </div>
    </div>
  );
};
